import { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import LineFloatingButton from '../../components/feature/LineFloatingButton';
import BlogDetailContent from './components/BlogDetailContent';
import SeoHead from '../../components/feature/SeoHead';
import { supabase } from '../../lib/supabaseClient';
import { BlogPost } from '../../types/blog';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) return;
    const fetchPost = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('8a4f04ed_blog_posts')
        .select('*, category:8a4f04ed_blog_categories(*)')
        .eq('id', id)
        .eq('published', true)
        .maybeSingle();
      if (error || !data) {
        setNotFound(true);
      } else {
        setPost(data as BlogPost);
      }
      setLoading(false);
    };
    fetchPost();
  }, [id]);

  const articleJsonLd = useMemo(() => {
    if (!post) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      url: `${SITE_URL}/blog/${id}`,
      headline: post.title,
      description: post.excerpt || post.title,
      image: post.thumbnail_url || undefined,
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at || post.created_at,
      publisher: {
        '@type': 'Organization',
        name: '縁結び 和はは',
        url: SITE_URL,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/blog/${id}`,
      },
    };
  }, [post, id]);

  if (!loading && notFound) return <Navigate to="/blog" replace />;

  return (
    <>
      {post && (
        <SeoHead
          title={`${post.title} | 縁結び 和はは`}
          description={post.excerpt || `${post.title} - 縁結び和ははのシニア婚活コラム`}
          path={`/blog/${id}`}
          jsonLd={articleJsonLd ?? undefined}
        />
      )}
      {!post && !loading && (
        <SeoHead
          title="ブログ記事 | 縁結び 和はは"
          description="縁結び和ははのシニア婚活コラム記事"
          path={`/blog/${id}`}
        />
      )}
      <Navbar />
      <main style={{ paddingTop: '72px' }}>
        {loading ? (
          <div className="flex items-center justify-center min-h-[60vh]" style={{ background: '#fff8f2' }}>
            <div className="flex items-center gap-3" style={{ color: '#9b8b7f' }}>
              <span className="flex items-center justify-center w-6 h-6">
                <i className="ri-loader-4-line text-2xl animate-spin" style={{ color: '#5a9e6f' }}></i>
              </span>
              読み込み中...
            </div>
          </div>
        ) : post ? (
          <BlogDetailContent post={post} />
        ) : null}
      </main>
      <Footer />
      <LineFloatingButton />
    </>
  );
}
